<template>
  <div class="container">
    <div class="box-title">
      <h4>
        <i class="fal fa-shipping-fast"></i>
        {{ $t("fleetTrips.board_title") }}
        <span class="countTotal">({{ count }})</span>
        <span class="btn-add">
          <vs-button flat @click="ToggleCopyPanel()">
            <i class="fal fa-copy" :style="{marginRight: '5px'}"></i> {{ $t("fleetTrips.copy_plan_from_other_day") }}
          </vs-button>
        </span>
      </h4>
    </div>

    <!-- Copy a whole day's plan → today (or the selected board date) as PENDING
         trips for review. -->
    <div class="box copy-panel" v-if="showCopyPanel">
      <label class="label"><i class="fal fa-copy"></i> {{ $t("fleetTrips.copy_panel_title") }}</label>
      <div class="columns" :style="{alignItems: 'flex-end'}">
        <div class="column is-3">
          <label class="label">{{ $t("fleetTrips.copy_source_date") }}</label>
          <date-picker
              format="DD/MM/YYYY"
              value-type="YYYY-MM-DD"
              type="date"
              v-model="copySourceDate"
              :clearable="false"
              @change="RunCopyPreview"
          ></date-picker>
        </div>
        <div class="column is-3">
          <label class="label">{{ $t("fleetTrips.copy_target_date") }}</label>
          <date-picker
              format="DD/MM/YYYY"
              value-type="YYYY-MM-DD"
              type="date"
              v-model="copyTargetDate"
              :clearable="false"
              @change="RunCopyPreview"
          ></date-picker>
        </div>
        <div class="column is-6">
          <p :style="{fontSize: '12px', color: '#666'}">
            {{ $t("fleetTrips.copy_note_prefix") }} <b :style="{color: '#e8a10f'}">{{ $t("fleetTrips.pending_approval") }}</b>
            {{ $t("fleetTrips.copy_note_suffix") }}
          </p>
        </div>
      </div>

      <Loading v-if="copyPreviewLoading"/>
      <div v-if="copyPreview && !copyPreviewLoading" :style="{marginTop: '8px'}">
        <p :style="{fontSize: '13px'}">
          {{ $t("fleetTrips.will_create") }} <b :style="{color: '#2dcc70'}">{{ copyPreview.would_create.length }}</b> {{ $t("fleetTrips.trips_unit") }} ·
          {{ $t("fleetTrips.skip") }} <b :style="{color: '#e8a10f'}">{{ copyPreview.would_skip.length }}</b> {{ $t("fleetTrips.trips_unit") }}
        </p>

        <!-- Nothing NEW to copy: say WHY (copy dedups by route+round on the
             target day) so an all-skipped result isn't mistaken for "empty". -->
        <p v-if="!copyPreview.would_create.length"
           :style="{fontSize: '12px', color: '#c0392b', background: '#fffaf0', padding: '8px', borderRadius: '6px'}">
          <template v-if="copyPreview.would_skip.length">
            {{ $t("fleetTrips.copy_all_exist", {
              source: FleetFormatDate(copyPreview.source_date),
              target: FleetFormatDate(copyPreview.target_date),
            }) }}
          </template>
          <template v-else>
            {{ $t("fleetTrips.copy_source_empty", {source: FleetFormatDate(copyPreview.source_date)}) }}
          </template>
        </p>

        <table class="mini-table" v-if="copyPreview.would_create.length">
          <thead>
          <tr>
            <th>{{ $t("fleetTrips.route") }}</th>
            <th>{{ $t("fleetTrips.round") }}</th>
            <th>{{ $t("fleetTrips.plate") }}</th>
            <th>{{ $t("fleetTrips.driver") }}</th>
            <th>{{ $t("fleetTrips.departure_time") }}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(row, index) in copyPreview.would_create" :key="'c' + index">
            <td>
              <span :style="{color: 'crimson'}">{{ row.route_code || '' }}</span>
              {{ row.route_name }}
            </td>
            <td :style="{textAlign: 'center'}">{{ row.round_number }}</td>
            <td>
              <span v-if="row.truck_plate">{{ row.truck_plate }}</span>
              <span v-else class="no-truck-chip">{{ $t("fleetTrips.no_truck_yet") }}</span>
            </td>
            <td :style="{textAlign: 'center'}">{{ row.driver_count }}</td>
            <td>{{ FleetFormatTime(row.planned_departure_at) }}</td>
          </tr>
          </tbody>
        </table>
        <!-- Skipped rounds shown as a (dimmed) table so a "found but already
             exists on the target day" result is visible, not hidden. -->
        <div v-if="copyPreview.would_skip.length" :style="{marginTop: '8px'}">
          <p :style="{fontSize: '12px', color: '#e8a10f', marginBottom: '4px'}">
            {{ $t("fleetTrips.skipped_trips_title") }}
          </p>
          <table class="mini-table" :style="{opacity: '0.7'}">
            <thead>
            <tr>
              <th>{{ $t("fleetTrips.route") }}</th>
              <th>{{ $t("fleetTrips.round") }}</th>
              <th>{{ $t("fleetTrips.reason") }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(row, index) in copyPreview.would_skip" :key="'s' + index">
              <td>
                <span :style="{color: 'crimson'}">{{ row.route_code || '' }}</span>
                {{ row.route_name }}
              </td>
              <td :style="{textAlign: 'center'}">{{ row.round_number }}</td>
              <td :style="{color: '#e8a10f'}">{{ SkipReasonLabel(row.reason) }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div :style="{marginTop: '12px', display: 'flex', gap: '10px'}">
        <vs-button flat :loading="btnLoading" :disabled="!copyPreview || !copyPreview.would_create.length"
                   @click="ConfirmCopyDay()">
          {{ $t("fleetTrips.copy_n_trips", {n: copyPreview ? copyPreview.would_create.length : 0}) }}
        </vs-button>
        <vs-button flat danger @click="ToggleCopyPanel()">{{ $t("fleetTrips.cancel") }}</vs-button>
      </div>
    </div>

    <!-- Review bar: bulk-enable the pending trips currently on the board. -->
    <div class="box pending-bar" v-if="pendingTrips.length">
      <span>
        <i class="fal fa-clock" :style="{color: '#e8a10f'}"></i>
        {{ $t("fleetTrips.pending_bar_prefix") }} <b>{{ pendingTrips.length }}</b> {{ $t("fleetTrips.pending_bar_suffix") }}
      </span>
      <vs-button flat :loading="btnLoading" @click="ApproveAllPending()">
        <i class="fal fa-check-double" :style="{marginRight: '5px'}"></i> {{ $t("fleetTrips.approve_all") }}
      </vs-button>
    </div>

    <div class="box">
      <div class="columns">
        <div class="column is-3">
          <div class="control field">
            <label class="label">{{ $t("fleetTrips.date") }}:</label>
            <date-picker
                format="DD/MM/YYYY"
                value-type="YYYY-MM-DD"
                type="date"
                v-model="boardDate"
                :clearable="false"
                @change="OnFilterChange"
            ></date-picker>
          </div>
        </div>
        <div class="column is-4">
          <div class="control">
            <label class="label">{{ $t("fleetTrips.route") }}</label>
            <model-list-select
                :list="boardRouteOptions"
                v-model="boardRouteId"
                :placeholder="$t('common.all')"
                option-text="label"
                option-value="id"
                @input="OnFilterChange"
            >
            </model-list-select>
          </div>
        </div>
        <div class="column is-2">
          <div class="control">
            <label class="label">{{ $t("fleetTrips.status") }}</label>
            <div class="select">
              <select class="input" v-model="boardStatus" @change="OnFilterChange">
                <option v-for="(status, index) in tripStatusOptions" :key="index" :value="status.id">
                  {{ status.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="control">
            <label class="label">{{ $t("fleetTrips.approval") }}</label>
            <div class="select">
              <select class="input" v-model="boardApproved" @change="OnFilterChange">
                <option value="">{{ $t("common.all") }}</option>
                <option value="0">{{ $t("fleetTrips.pending_approval") }}</option>
                <option value="1">{{ $t("fleetTrips.approved") }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="center">
      <vs-table>
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t("fleetTrips.manage") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.trip_code") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.route") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.round") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.truck_plate") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.driver") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.departure_time") }}</vs-th>
            <vs-th :title="$t('fleetTrips.on_truck_tooltip')">{{ $t("fleetTrips.bills_on_truck") }}</vs-th>
            <vs-th :title="$t('fleetTrips.on_truck_tooltip')">{{ $t("fleetTrips.pieces_on_truck") }}</vs-th>
            <vs-th :title="$t('fleetTrips.on_truck_tooltip')">{{ $t("fleetTrips.weight_on_truck_kg") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.status") }}</vs-th>
            <vs-th>{{ $t("fleetTrips.approve") }}</vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr :key="index" :data="trip" v-for="(trip, index) in trips"
                 :style="trip.is_approved === false ? {background: '#fffaf0'} : {}">
            <vs-td style="width: 92px; white-space: nowrap">
              <vs-button v-if="trip.direction !== 'return' && !trip.paired_trip_id"
                         circle icon flat :loading="btnLoading"
                         :title="$t('fleetTrips.create_return_trip_tooltip')" @click="MakeReturnTrip(trip)">
                <i class="fal fa-undo" :style="{color: '#0aa'}"></i>
              </vs-button>
              <vs-button circle icon flat :title="$t('fleetTrips.view_trip_detail')" @click="GoToTripDetail(trip.id)">
                <i class="fal fa-eye"></i>
              </vs-button>
            </vs-td>
            <vs-td :style="{color: 'crimson', fontWeight: 'bold'}">{{ trip.trip_code }}</vs-td>
            <vs-td :style="{color: '#195BFF'}">{{ trip.route_name }}</vs-td>
            <vs-td :style="{textAlign: 'center'}">
              {{ trip.round_number }}
              <span v-if="trip.direction" class="dir-chip"
                    :class="trip.direction === 'return' ? 'dir-return' : 'dir-outbound'">
                <i :class="FleetDirectionIcon(trip.direction)"></i>
                {{ FleetDirectionLabel(trip.direction) }}
              </span>
            </vs-td>
            <vs-td>
              <span v-if="trip.truck_plate">{{ trip.truck_plate }}</span>
              <span v-else class="no-truck-chip">{{ $t("fleetTrips.no_truck_yet") }}</span>
            </vs-td>
            <vs-td>
              <template v-if="trip.drivers && trip.drivers.length">
                <div v-for="(d, di) in trip.drivers" :key="di"
                     :style="{fontSize: '12px', lineHeight: '1.5'}">
                  <span :style="{color: d.driver_role === 'primary' ? '#e21312' : '#888'}">
                    {{ d.driver_role === 'primary' ? $t("fleetTrips.driver_role_primary") : $t("fleetTrips.driver_role_crew") }}
                  </span>
                  {{ d.driver_name || DriverName(d.driver_id) }}
                  <b :style="{color: '#e21312'}">{{ d.driver_card_id || DriverCardId(d.driver_id) }}</b>
                </div>
              </template>
              <span v-else :style="{color: 'grey'}">...</span>
            </vs-td>
            <vs-td>{{ FleetFormatTime(trip.planned_departure_at) }}</vs-td>
            <vs-td>{{ FleetTripTotals(trip).bills || 0 }}</vs-td>
            <vs-td>
              {{ FleetTripTotals(trip).pieces || 0 }}
              <span v-if="trip.totals && trip.totals.off_route && trip.totals.off_route.pieces"
                    :style="{color: FleetMovementColor('offRoute'), fontSize: '11px', fontWeight: 700}"
                    :title="$t('fleetTrips.off_route_tooltip')">
                {{ FleetOffRouteText(trip.totals.off_route.pieces, trip.totals.off_route.on_truck_pieces) }}
              </span>
            </vs-td>
            <vs-td>{{ FleetFormatNumber(FleetTripTotals(trip).weight_kg || 0, 1) }}</vs-td>
            <vs-td>
              <span class="status-chip" :style="{background: FleetTripStatusColor(trip.status)}">
                {{ FleetTripStatusLabel(trip.status) }}
              </span>
              <!-- GPS integrity review flags (001-fleet-gps-tracking): the
                   close-time gps_integrity block flagged something (fake GPS /
                   dark legs / no data). Crimson when a confirmed signal
                   (mocked/implausible fixes) is among them, amber otherwise.
                   Details live on the trip-detail card — this is just the
                   "look here" marker. -->
              <i v-if="FleetGpsFlags(trip).length"
                 class="fas fa-exclamation-triangle gps-board-flag"
                 :style="{color: GpsBoardFlagColor(trip)}"
                 :title="$t('fleetTrips.gps_abnormal', {flags: FleetGpsFlagsTitle(trip)})"></i>
            </vs-td>
            <vs-td>
              <template v-if="trip.is_approved === false">
                <vs-button flat size="small" :loading="btnLoading" @click="ApprovePendingTrip(trip)">
                  <i class="fal fa-check" :style="{marginRight: '4px'}"></i> {{ $t("fleetTrips.approve") }}
                </vs-button>
              </template>
              <span v-else-if="trip.status === 'planned'"
                    :style="{color: '#2dcc70', fontSize: '12px', cursor: 'pointer'}"
                    :title="$t('fleetTrips.unapprove_tooltip')"
                    @click="UnapprovePendingTrip(trip)">
                <i class="fal fa-check-circle"></i> {{ $t("fleetTrips.approved") }}
              </span>
              <span v-else :style="{color: '#9aa', fontSize: '12px'}">
                <i class="fal fa-check-circle"></i>
              </span>
            </vs-td>
          </vs-tr>
        </template>
        <template #footer>
          <Pagination v-if="pagination.last_page > 1" :pagination="pagination" :offset="offset"
                      @paginate="OnPaginate()"></Pagination>
          <Loading v-if="isLoading"/>
          <p v-if="!isLoading && !trips.length" :style="{textAlign: 'center', color: 'grey', padding: '15px'}">
            {{ $t("fleetTrips.no_trips_on_date") }}
          </p>
        </template>
      </vs-table>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import {ModelListSelect} from 'vue-search-select';
import Loading from '@views/Loading';
import Pagination from '@/Plugins/Paginations/Pagination';
import MixinFleetTrips from '@/Mixins/Fleet/Trips';
import {FLEET_ERROR_MESSAGES} from '@/Mixins/Fleet/FleetHelpers';

export default {
  components: {
    DatePicker,
    ModelListSelect,
    Loading,
    Pagination,
  },
  mixins: [MixinFleetTrips],
  data: () => ({
    // Copy-day panel
    showCopyPanel: false,
    copySourceDate: moment(new Date()).subtract(1, 'days').format('YYYY-MM-DD'),
    copyTargetDate: moment(new Date()).format('YYYY-MM-DD'),
    copyPreview: null,
    copyPreviewLoading: false,
    // driver id -> name lookup for the ຄົນຂັບ column (the trip payload carries
    // only driver_id/driver_role, not names).
    drivers: [],
  }),
  computed: {
    boardRouteOptions() {
      return this.boardRoutes.map((route) => ({
        ...route,
        label: route.code ? `${route.code} · ${route.name}` : route.name,
      }));
    },
    // Pending (not-yet-approved) trips visible on the current page.
    pendingTrips() {
      return this.trips.filter((trip) => trip.is_approved === false);
    },
  },
  methods: {
    // Crimson when any OS/filter-CONFIRMED signal (fake GPS, implausible
    // fixes, stop-only pattern) is present; amber for coverage-only flags.
    // Keyed on the vocabulary's `severity`, never its colour strings, so a
    // theme tweak or an unknown future flag can't silently change severity.
    GpsBoardFlagColor(trip) {
      const flags = this.FleetGpsFlags(trip);
      const confirmed = flags.some((f) => this.FleetGpsFlagSeverity(f) === 'confirmed');
      return confirmed ? '#e21312' : '#e8a10f';
    },
    // Filters live in the URL query so trip detail -> back keeps the view.
    SyncQuery() {
      const query = {};
      if (this.boardDate) query.date = this.boardDate;
      if (this.boardRouteId) query.route_id = String(this.boardRouteId);
      if (this.boardStatus) query.status = String(this.boardStatus);
      if (this.boardApproved !== '') query.approved = String(this.boardApproved);
      const page = (this.pagination && this.pagination.current_page) || 1;
      if (page > 1) query.page = String(page);
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({query}).catch(() => {});
      }
    },
    OnFilterChange() {
      this.pagination.current_page = 1;
      this.SyncQuery();
      this.FetchTrips();
    },
    OnPaginate() {
      this.SyncQuery();
      this.FetchTrips();
    },
    GoToTripDetail(tripId) {
      this.$router.push({name: 'fleet.trips.detail', params: {id: tripId}});
    },

    /** ---------------- Copy day ---------------- */
    ToggleCopyPanel() {
      this.showCopyPanel = !this.showCopyPanel;
      if (this.showCopyPanel) {
        // Default: copy the day before the board date INTO the board date.
        this.copyTargetDate = this.boardDate || moment(new Date()).format('YYYY-MM-DD');
        this.copySourceDate = moment(this.copyTargetDate).subtract(1, 'days').format('YYYY-MM-DD');
        this.copyPreview = null;
        this.RunCopyPreview();
      }
    },
    RunCopyPreview() {
      if (!this.copySourceDate || !this.copyTargetDate) return;
      if (this.copySourceDate === this.copyTargetDate) {
        this.copyPreview = null;
        this.$notification.warning({message: this.$t('fleetTrips.copy_dates_must_differ')});
        return;
      }
      this.copyPreviewLoading = true;
      this.copyPreview = null;
      this.PreviewCopyDay(this.copySourceDate, this.copyTargetDate).then((preview) => {
        this.copyPreview = preview;
        this.copyPreviewLoading = false;
      }).catch(() => {
        this.copyPreviewLoading = false;
      });
    },
    ConfirmCopyDay() {
      this.CopyDay(this.copySourceDate, this.copyTargetDate).then((res) => {
        const created = res.created_count || 0;
        const skipped = (res.skipped || []).length;
        this.$notification.success({
          message: this.$t('fleetTrips.copied_n_trips_pending', {n: created})
              + (skipped ? ' ' + this.$t('fleetTrips.skipped_n_suffix', {n: skipped}) : ''),
          duration: 5000,
        });
        this.showCopyPanel = false;
        // Jump the board to the target day + show the pending queue.
        this.boardDate = this.copyTargetDate;
        this.boardApproved = '0';
        this.pagination.current_page = 1;
        this.SyncQuery();
        this.FetchTrips();
      }).catch(() => {
      });
    },
    SkipSummary(skipped) {
      return skipped.map((row) => {
        const reason = FLEET_ERROR_MESSAGES[row.reason] || row.reason;
        return this.$t('fleetTrips.skip_summary_row', {route: row.route_code || '', round: row.round_number, reason});
      }).join(', ');
    },
    SkipReasonLabel(reason) {
      return FLEET_ERROR_MESSAGES[reason] || reason;
    },
    /** Driver id -> "name (phone)" for the ຄົນຂັບ column (self-contained,
     * same source as the round planner). */
    FetchDrivers() {
      this.$axios.get('list-vehicle-department-users', {params: {per_page: 1000}}).then((res) => {
        const rows = (res.data.listVehicleDepartmentUsers && res.data.listVehicleDepartmentUsers.data) || [];
        this.drivers = rows.map((row) => ({
          id: row.vehicleDepartmentUserId,
          cardId: row.userCardid,
          name: `${row.userName || '...'}${row.userTel ? ' (' + row.userTel + ')' : ''}`,
        }));
      }).catch(() => {
      });
    },
    DriverName(driverId) {
      const driver = this.drivers.find((d) => d.id === driverId);
      return driver ? driver.name : `#${driverId}`;
    },
    // The human-searchable card id (users.user_card_id), resolved from the
    // internal user_vehicle_departments.id. Falls back to the internal id.
    DriverCardId(driverId) {
      const driver = this.drivers.find((d) => d.id === driverId);
      return (driver && driver.cardId) ? driver.cardId : `#${driverId}`;
    },

    /** ---------------- Approval ---------------- */
    ApprovePendingTrip(trip) {
      this.ApproveTrip(trip.id, true).then(() => {
        this.FetchTrips();
      }).catch(() => {
      });
    },
    UnapprovePendingTrip(trip) {
      this.ApproveTrip(trip.id, false).then(() => {
        this.FetchTrips();
      }).catch(() => {
      });
    },
    ApproveAllPending() {
      const ids = this.pendingTrips.map((trip) => trip.id);
      if (!ids.length) return;
      this.ApproveTripsBatch(ids, true).then((res) => {
        this.$notification.success({message: this.$t('fleetTrips.approved_n_trips', {n: res.updated})});
        this.FetchTrips();
      }).catch(() => {
      });
    },
    // Create the return (ຂາກັບ) leg for an outbound trip that has no pair.
    MakeReturnTrip(trip) {
      this.CreateReturnTrip(trip.id).then(() => {
        this.FetchTrips();
      }).catch(() => {
      });
    },
  },
  created() {
    const query = this.$route.query || {};
    if (query.date) this.boardDate = query.date;
    if (query.route_id) this.boardRouteId = Number(query.route_id) || '';
    if (query.status) this.boardStatus = query.status;
    if (query.approved !== undefined && query.approved !== '') this.boardApproved = query.approved;
    if (query.page) this.pagination.current_page = Number(query.page) || 1;

    this.FetchBoardRoutes();
    this.FetchDrivers();
    this.FetchTrips();
  },
};
</script>

<style scoped>
.no-truck-chip {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  background: #fff4e0;
  color: #b26a00;
  font-size: 12px;
}
.status-chip {
  color: white;
  border-radius: 12px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
}

.gps-board-flag {
  margin-left: 6px;
  font-size: 14px;
  vertical-align: middle;
  cursor: help;
}

.dir-chip {
  display: inline-block;
  margin-left: 5px;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: bold;
  white-space: nowrap;
}

.dir-outbound {
  background: rgba(25, 91, 255, 0.12);
  color: #195BFF;
}

.dir-return {
  background: rgba(0, 170, 170, 0.14);
  color: #0a9;
}

.copy-panel {
  border-left: 3px solid #195BFF;
}

.pending-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fffaf0;
  border: 1px solid #f3d488;
}

.mini-table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;
  margin-top: 6px;
}

.mini-table th, .mini-table td {
  padding: 4px 6px;
  border-bottom: 1px solid #f0f0f0;
  text-align: left;
}

.mini-table th {
  color: #666;
}
</style>
